// Derives which KB components are active for an equipment instance from its declared
// variants — a display-only mirror of the agent's filter_components_for_variants()
// (services/agent/src/agent_domain/knowledge_base/evaluator.py), per
// docs/spec/knowledge-base.md § Variants file. The agent stays the source of truth
// for evaluation/completeness; this only decides what Rakenne renders.
import {
  type KBComponent,
  type KBConfigOptionGroup,
  type KBStream,
} from "@/lib/api/agent";

// A component is active iff it is not `optional`, or a declared variant's
// enables_components names it — and no declared variant's disables_components excludes it.
// An `optional` component with no gating choice anywhere in the KB stays always-present
// (a no-op), matching the backend rule exactly.
export function deriveActiveComponentIds(
  components: KBComponent[],
  optionGroups: KBConfigOptionGroup[],
  declaredVariants: string[],
): Set<string> {
  if (declaredVariants.length === 0) {
    return new Set(components.map((c) => c.id));
  }
  const declared = new Set(declaredVariants);
  const gated = new Set<string>();
  const enabled = new Set<string>();
  const disabled = new Set<string>();

  function apply(isDeclared: boolean, enables: string[], disables: string[]) {
    for (const c of enables) gated.add(c);
    if (!isDeclared) return;
    for (const c of enables) enabled.add(c);
    for (const c of disables) disabled.add(c);
  }

  for (const group of optionGroups) {
    if (group.type === "boolean") {
      apply(
        declared.has(group.id),
        group.enables_components,
        group.disables_components,
      );
    }
    for (const choice of group.choices) {
      apply(
        declared.has(choice.id),
        choice.enables_components,
        choice.disables_components,
      );
    }
  }

  return new Set(
    components
      .filter(
        (c) =>
          !disabled.has(c.id) &&
          (!c.optional || !gated.has(c.id) || enabled.has(c.id)),
      )
      .map((c) => c.id),
  );
}

// The active set in process-path order: the instance's declared_component_order first
// (dropping any stale/inactive id — never an error), then any remaining active component in the KB type's own
// catalog order (the `components[]` array order the API already returns sorted by).
export function orderedActiveComponents(
  components: KBComponent[],
  activeIds: Set<string>,
  declaredOrder: string[],
): KBComponent[] {
  const byId = new Map(components.map((c) => [c.id, c]));
  const ordered: KBComponent[] = [];
  const seen = new Set<string>();
  for (const id of declaredOrder) {
    if (!activeIds.has(id) || seen.has(id)) continue;
    const c = byId.get(id);
    if (c) {
      ordered.push(c);
      seen.add(id);
    }
  }
  for (const c of components) {
    if (activeIds.has(c.id) && !seen.has(c.id)) {
      ordered.push(c);
      seen.add(c.id);
    }
  }
  return ordered;
}

// ── Stream grouping (#1153/#1157) ───────────────────────────────────────────────
//
// A type's `streams[]` (from `equipments/<type>/streams.yaml`, `GET /kb/equipment/{type}`)
// groups component ids by duct/pipe — the only stream catalog; nothing here re-derives or
// invents a second one. A component may belong to more than one stream (e.g. a heat-recovery
// element sits on both the supply and extract air ducts).

export interface ActiveStreamGroup {
  streamId: string;
  nameFi: string;
  order: number;
  components: KBComponent[];
}

// The active set per stream, in process-path order: the instance's per-stream override
// (`declared_stream_component_order[stream.id]`) first — dropping a stale/non-member/inactive
// id, same tolerance as `orderedActiveComponents` — then any remaining active member in the
// stream's own catalog order. A stream with no active member is omitted (an empty group has
// nothing to show or reorder).
export function activeComponentsByStream(
  components: KBComponent[],
  activeIds: Set<string>,
  streams: KBStream[],
  declaredStreamOrder: Record<string, string[]>,
): ActiveStreamGroup[] {
  const byId = new Map(components.map((c) => [c.id, c]));
  return [...streams]
    .sort((a, b) => a.order - b.order)
    .map((stream) => {
      const memberIds = new Set(stream.components);
      const override = declaredStreamOrder[stream.id] ?? [];
      const ordered: KBComponent[] = [];
      const seen = new Set<string>();
      for (const id of override) {
        if (seen.has(id) || !memberIds.has(id) || !activeIds.has(id)) continue;
        const c = byId.get(id);
        if (c) {
          ordered.push(c);
          seen.add(id);
        }
      }
      for (const id of stream.components) {
        if (seen.has(id) || !activeIds.has(id)) continue;
        const c = byId.get(id);
        if (c) {
          ordered.push(c);
          seen.add(id);
        }
      }
      return {
        streamId: stream.id,
        nameFi: stream.name_fi,
        order: stream.order,
        components: ordered,
      };
    })
    .filter((group) => group.components.length > 0);
}

// Move one component up/down within a single stream's displayed order, returning a new
// declared_stream_component_order map with only that stream's list changed — every other
// stream's entry is carried over untouched, since the API replaces the whole map on save
// (services/building EquipmentService.update_equipment) and losing an unrelated stream's
// saved order would be a silent regression.
export function moveComponentInStream(
  declaredStreamOrder: Record<string, string[]>,
  streamId: string,
  displayOrderIds: string[],
  componentId: string,
  direction: -1 | 1,
): Record<string, string[]> {
  const ids = [...displayOrderIds];
  const idx = ids.indexOf(componentId);
  const swapWith = idx + direction;
  if (idx < 0 || swapWith < 0 || swapWith >= ids.length) {
    return declaredStreamOrder;
  }
  [ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]];
  return { ...declaredStreamOrder, [streamId]: ids };
}

// First-stream-membership rank for every component id across a type's `streams[]` — a
// component's earliest stream (by `order`), then its index within that stream's own
// `components[]` list. Shared by Rakenne's component ordering and any other view that should
// read in the same process-path sequence (Kyvykkyydet/Signaalit component headers, #1157) —
// so neither invents a second stream-derived ordering.
export function streamMembershipRank(streams: KBStream[]): Map<string, number> {
  const rank = new Map<string, number>();
  for (const stream of [...streams].sort((a, b) => a.order - b.order)) {
    stream.components.forEach((componentId, index) => {
      if (!rank.has(componentId))
        rank.set(componentId, stream.order * 1000 + index);
    });
  }
  return rank;
}

// Reorder any id-bearing list to follow `streamMembershipRank` — components with no stream
// membership (or a type with no streams) keep their relative (catalog) order, sorted after
// every ranked component.
export function orderByStreamMembership<T extends { id: string }>(
  items: T[],
  streams: KBStream[],
): T[] {
  if (streams.length === 0) return items;
  const rank = streamMembershipRank(streams);
  return [...items].sort((a, b) => {
    const rankA = rank.get(a.id);
    const rankB = rank.get(b.id);
    if (rankA !== undefined && rankB !== undefined) return rankA - rankB;
    if (rankA !== undefined) return -1;
    if (rankB !== undefined) return 1;
    return 0;
  });
}
